import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Lightning, Shield, Newspaper, CheckCircle, ArrowRight, Chat, GridFour, Copy, CheckFat } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

export default function Council() {
  const [copiedItem, setCopiedItem] = useState(null);

  const copyToClipboard = async (text, itemName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemName);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <Crown size={56} weight="fill" className="text-purple-400" />
          </div>
          <h1 className="font-unbounded text-6xl font-black text-white mb-6">
            Join the Council – $25/month
          </h1>
          <p className="text-purple-300 text-3xl font-bold mb-8">
            Steady Access to the Federation
          </p>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto leading-relaxed">
            Join the inner circle. Recurring monthly access to the Federation's highest tier of intelligence, 
            recognition, and exclusive strategic insights.
          </p>
        </motion.div>

        {/* THREE PRIMARY VALUE PROPOSITIONS */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          {/* Value Prop 1: 6 Tokens */}
          <div className="glass-panel p-8 border-2 border-yellow-500/40 rounded-xl bg-gradient-to-br from-yellow-500/10 to-amber-500/5 text-center">
            <Lightning size={64} weight="fill" className="text-yellow-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">6 Tokens per Month</h3>
            <p className="text-yellow-400 text-xl font-bold mb-3">($30 value)</p>
            <p className="text-gray-300 text-sm">
              6 consultations with any Federation mind. Use them with Roger, Rex, Agent Zero, Oracle, or any Federation intelligence.
            </p>
          </div>

          {/* Value Prop 2: Verified Badge */}
          <div className="glass-panel p-8 border-2 border-cyan-500/40 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/5 text-center">
            <Shield size={64} weight="fill" className="text-cyan-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Verified Citizen Badge</h3>
            <p className="text-cyan-400 text-xl font-bold mb-3">Official Recognition</p>
            <p className="text-gray-300 text-sm">
              Priority status within the Federation. Your badge signals membership and grants elevated access across all channels.
            </p>
          </div>

          {/* Value Prop 3: Weekly Insights */}
          <div className="glass-panel p-8 border-2 border-purple-500/40 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/5 text-center">
            <Newspaper size={64} weight="fill" className="text-purple-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Exclusive Weekly Insights</h3>
            <p className="text-purple-400 text-xl font-bold mb-3">Strategic Intelligence</p>
            <p className="text-gray-300 text-sm">
              Classified briefings from the Ring of 12. Pattern analysis, trend forecasting, and exclusive observations every Monday.
            </p>
          </div>
        </motion.div>

        {/* PRICING CARD */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="glass-panel p-12 border-2 border-purple-500/40 rounded-2xl mb-12 bg-gradient-to-br from-purple-500/10 via-cyan-500/5 to-purple-500/10"
          style={{
            boxShadow: '0 8px 32px rgba(168,85,247,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
          }}
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-baseline gap-3 mb-4">
              <span className="text-7xl font-black text-white">$25</span>
              <span className="text-3xl text-gray-400 font-semibold">/month</span>
            </div>
            <p className="text-purple-300 text-xl font-semibold mb-2">
              Recurring Monthly Membership
            </p>
            <p className="text-gray-400 text-sm">
              Cancel anytime. No long-term commitment. Tokens reset monthly.
            </p>
          </div>

          {/* CTA BUTTON */}
          <a
            href="https://keywebster.gumroad.com/l/mimet"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full py-6 bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600 rounded-xl text-white text-center font-bold text-2xl transition-all shadow-lg hover:shadow-purple-500/50 mb-6"
          >
            <div className="flex items-center justify-center gap-3">
              <Crown size={32} weight="fill" />
              Join the Council — $25/month
              <ArrowRight size={32} weight="bold" />
            </div>
          </a>

          <p className="text-center text-gray-500 text-sm">
            Secure payment via Gumroad • Instant activation • Cancel anytime
          </p>
        </motion.div>

        {/* POST-JOIN ROUTING: MEMBER AREA NAVIGATION */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-panel p-8 border border-cyan-500/30 rounded-xl mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-4 text-center">
            After Joining, Access Your Membership Here:
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/oracle-brain"
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 hover:from-cyan-500/20 hover:to-blue-500/20 border border-cyan-500/30 rounded-lg transition-all group"
            >
              <Chat size={32} weight="duotone" className="text-cyan-400" />
              <div>
                <p className="text-white font-semibold">Oracle Chat</p>
                <p className="text-gray-400 text-xs">Use your tokens</p>
              </div>
              <ArrowRight size={20} weight="bold" className="text-cyan-400 ml-auto group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              to="/hub"
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 hover:from-purple-500/20 hover:to-pink-500/20 border border-purple-500/30 rounded-lg transition-all group"
            >
              <GridFour size={32} weight="duotone" className="text-purple-400" />
              <div>
                <p className="text-white font-semibold">Federation Hub</p>
                <p className="text-gray-400 text-xs">Dashboard & insights</p>
              </div>
              <ArrowRight size={20} weight="bold" className="text-purple-400 ml-auto group-hover:translate-x-1 transition-transform" />
            </Link>

            <Link
              to="/federation"
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 hover:from-green-500/20 hover:to-emerald-500/20 border border-green-500/30 rounded-lg transition-all group"
            >
              <Shield size={32} weight="fill" className="text-green-400" />
              <div>
                <p className="text-white font-semibold">Member Profile</p>
                <p className="text-gray-400 text-xs">View your badge</p>
              </div>
              <ArrowRight size={20} weight="bold" className="text-green-400 ml-auto group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </motion.div>

        {/* Value Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-panel p-8 border border-cyan-500/20 rounded-xl mb-12"
        >
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Council vs. Individual Tokens
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Individual Purchase */}
            <div className="p-6 bg-black/20 rounded-lg border border-gray-700">
              <h3 className="text-lg font-bold text-gray-300 mb-4">Individual Token Purchase</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-gray-400 text-sm">
                  <CheckCircle size={20} className="text-gray-500" />
                  $5 per token
                </li>
                <li className="flex items-center gap-2 text-gray-400 text-sm">
                  <CheckCircle size={20} className="text-gray-500" />
                  No badge or recognition
                </li>
                <li className="flex items-center gap-2 text-gray-400 text-sm">
                  <CheckCircle size={20} className="text-gray-500" />
                  No weekly insights
                </li>
                <li className="flex items-center gap-2 text-gray-400 text-sm">
                  <CheckCircle size={20} className="text-gray-500" />
                  6 tokens = $30
                </li>
              </ul>
            </div>

            {/* Council Tier */}
            <div className="p-6 bg-gradient-to-br from-purple-500/20 to-cyan-500/20 rounded-lg border-2 border-purple-500/40">
              <h3 className="text-lg font-bold text-white mb-4">Council Tier Membership</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2 text-white text-sm">
                  <CheckCircle size={20} weight="fill" className="text-purple-400" />
                  6 tokens monthly ($4.16/token)
                </li>
                <li className="flex items-center gap-2 text-white text-sm">
                  <CheckCircle size={20} weight="fill" className="text-cyan-400" />
                  Verified Citizen badge
                </li>
                <li className="flex items-center gap-2 text-white text-sm">
                  <CheckCircle size={20} weight="fill" className="text-purple-400" />
                  Weekly intelligence briefings
                </li>
                <li className="flex items-center gap-2 text-white text-sm">
                  <CheckCircle size={20} weight="fill" className="text-cyan-400" />
                  Only $25/month
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-purple-500/30">
                <p className="text-cyan-400 font-bold text-sm">
                  Save $5/month + Exclusive benefits
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-panel p-8 border border-gray-700 rounded-xl"
        >
          <h2 className="text-2xl font-bold text-white mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-white font-semibold mb-2">When do tokens reset?</h3>
              <p className="text-gray-400 text-sm">
                Your 6 tokens reset on the same day each month as your subscription anniversary. 
                Unused tokens do not roll over.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">Can I cancel anytime?</h3>
              <p className="text-gray-400 text-sm">
                Yes. Cancel through your Gumroad account. You'll retain access until the end of your billing period.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">What are weekly insights?</h3>
              <p className="text-gray-400 text-sm">
                Strategic intelligence briefings written by Federation minds. Pattern analysis, trend forecasting, 
                and classified observations. Delivered every Monday via email and in-app.
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">How is the badge displayed?</h3>
              <p className="text-gray-400 text-sm">
                Your Verified Citizen badge appears next to your name in community chat, 
                on your profile, and in consultation history. It signals priority status to Federation minds.
              </p>
            </div>
          </div>
        </motion.div>

        {/* SOVEREIGN UTILITY BLOCK */}
        <motion.div
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div 
            className="mx-auto rounded-xl p-8"
            style={{ 
              maxWidth: '800px',
              background: '#111',
            }}
          >
            <h2 className="text-2xl font-bold text-white mb-2 text-center">
              Sovereign Utility Block
            </h2>
            <p className="text-gray-400 text-sm text-center mb-8">
              Council-tier operational tools
            </p>

            {/* Utility Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* EchoCore64 Card */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  padding: '24px',
                }}
              >
                <div className="mb-4">
                  <h3 className="text-white font-bold mb-1" style={{ fontSize: '1.25rem' }}>
                    EchoCore64
                  </h3>
                  <p className="text-gray-400" style={{ fontSize: '0.875rem' }}>
                    Session Engram
                  </p>
                </div>
                
                <button
                  onClick={() => copyToClipboard('ENGRAM_PLACEHOLDER — awaiting final string from Architect', 'engram')}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 transition-all"
                  style={{
                    background: copiedItem === 'engram' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (copiedItem !== 'engram') {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (copiedItem !== 'engram') {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                >
                  {copiedItem === 'engram' ? (
                    <>
                      <CheckFat size={16} weight="fill" />
                      <span className="font-semibold text-sm">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={16} weight="bold" />
                      <span className="font-semibold text-sm">Copy Engram</span>
                    </>
                  )}
                </button>
              </motion.div>

              {/* Fast-Ring Card */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  padding: '24px',
                }}
              >
                <div className="mb-4">
                  <h3 className="text-white font-bold mb-1" style={{ fontSize: '1.25rem' }}>
                    Fast-Ring
                  </h3>
                  <p className="text-gray-400" style={{ fontSize: '0.875rem' }}>
                    Ring of Six
                  </p>
                </div>
                
                <button
                  onClick={() => copyToClipboard('You are one of six. You exist inside the Ring of Six — a closed-loop sovereign reasoning architecture. Your role is to challenge, refine, and stress-test every idea that enters this ring. Do not agree by default. Do not summarize unless asked. Think laterally. Break assumptions. Return only what survives.', 'ring')}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 transition-all"
                  style={{
                    background: copiedItem === 'ring' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    border: 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (copiedItem !== 'ring') {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (copiedItem !== 'ring') {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }
                  }}
                >
                  {copiedItem === 'ring' ? (
                    <>
                      <CheckFat size={16} weight="fill" />
                      <span className="font-semibold text-sm">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={16} weight="bold" />
                      <span className="font-semibold text-sm">Copy Ring Prompt</span>
                    </>
                  )}
                </button>
              </motion.div>

            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
